import { supabase } from '@/integrations/supabase/client';
import { createUuid } from '@/lib/uuid';

function dataUrlToFile(dataUrl: string, filename: string): File {
  const arr = dataUrl.split(',');
  const mime = arr[0].match(/:(.*?);/)?.[1] || 'image/jpeg';
  const bstr = atob(arr[1]);
  let n = bstr.length;
  const u8arr = new Uint8Array(n);
  while (n--) {
    u8arr[n] = bstr.charCodeAt(n);
  }
  return new File([u8arr], filename, { type: mime });
}

export function isImageDataUrl(value: unknown): value is string {
  return typeof value === 'string' && value.startsWith('data:image');
}

export type UploadResult = { url: string; path: string };

/**
 * Uploads a data URL to Storage under `{userId}/{folder}/{uuid}.jpg`.
 *
 * `userId` is required so offline-sync uploads land in the owner's folder even
 * when they are performed by a background sync loop with the session set up.
 * Returns the public URL and the storage path; the public URL only resolves
 * while buckets remain public — callers that render this URL later should
 * feed it through {@link resolveMediaUrl} to sign it.
 */
export async function uploadImageDataUrl(
  dataUrl: string,
  bucket: string,
  folder: string,
  userId: string,
  scopeId: string = userId,
): Promise<UploadResult> {
  if (!userId) {
    throw new Error('uploadImageDataUrl requires userId to build a scoped path');
  }
  const objectName = `${scopeId}/${folder}/${createUuid()}.jpg`;
  const file = dataUrlToFile(dataUrl, objectName);

  const { data, error } = await supabase.storage.from(bucket).upload(objectName, file, {
    cacheControl: '3600',
    upsert: false,
  });

  if (error || !data?.path) {
    throw error || new Error('Upload failed');
  }

  const { data: urlData } = supabase.storage.from(bucket).getPublicUrl(data.path);

  if (!urlData?.publicUrl) {
    throw new Error('Failed to get public URL');
  }

  return { url: urlData.publicUrl, path: data.path };
}

/**
 * If `imageUrl` is a data URL, upload it to Storage and return the resulting
 * URL. If it's already a Storage/http URL (or null), return it unchanged.
 *
 * Used at online save time to convert freshly-added `GeneralPhotosSection`
 * data URLs into Storage URLs before writing to `quote_photos.image_url`
 * (which has a CHECK constraint capping length at 2000 chars).
 */
export async function ensureStorageUrl(
  imageUrl: string | null | undefined,
  bucket: string,
  folder: string,
  userId: string
): Promise<string | null> {
  if (!imageUrl) return null;
  if (!isImageDataUrl(imageUrl)) return imageUrl;
  const uploaded = await uploadImageDataUrl(imageUrl, bucket, folder, userId);
  return uploaded.url;
}
